/**
 * Typed access to the roles & permissions added in
 * drizzle/migrations/0012_access_control_foundation.sql.
 *
 * Built-in roles (admin, instructor, sponsor) still live in user_roles and are
 * still checked with has_role(); custom roles and scoped assignments live in
 * access_role_assignments. All writes go through SECURITY DEFINER RPCs, which
 * enforce roles.manage, block granting permissions the caller lacks, and keep
 * at least one admin.
 *
 * The cast is required only because these tables and functions post-date the
 * checked-in generated Supabase types; it can be removed once types are
 * regenerated.
 */

import { supabase } from "@/integrations/supabase/client";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type ScopeType = "region" | "program" | "course";
export type ScopeMode = "global" | "scoped" | "everywhere";

export interface AccessPermission {
  key: string;
  category: string;
  label: string;
  description: string;
  scope_mode: ScopeMode;
  enforced: boolean;
  sort_order: number;
}

export interface AccessRole {
  id: string;
  key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  assignable: boolean;
  permissions: string[];
}

export interface AccessPerson {
  id: string;
  full_name: string;
  email: string;
}

export interface AccessAssignment {
  source: "system" | "custom";
  id: string;
  role_id: string;
  role_key: string;
  role_name: string;
  scope_type: ScopeType | null;
  scope_id: string | null;
  scope_label: string | null;
  expires_at: string | null;
  granted_at: string | null;
  person: AccessPerson | null;
}

export interface AccessAuditEntry {
  id: string;
  action: string;
  role_key: string | null;
  scope_label: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
  actor: AccessPerson | null;
  person: AccessPerson | null;
}

export interface ScopeOption {
  id: string;
  label: string;
}

export type ScopeOptions = Record<ScopeType, ScopeOption[]>;

export interface MyPermission {
  permission_key: string;
  scope_type: ScopeType | null;
  scope_id: string | null;
}

// React Query keys; everything under ["admin", "access"] is refetched after a change.
export const ACCESS_ASSIGNMENTS_KEY = ["admin", "access", "assignments"];
export const ACCESS_ROLES_KEY = ["admin", "access", "roles"];

export const SCOPE_TYPE_LABELS: Record<ScopeType, string> = {
  region: "Region",
  program: "Program",
  course: "Course",
};

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await db.rpc(fn, args);
  if (error) throw error;
  return data as T;
}

export async function fetchPermissions(): Promise<AccessPermission[]> {
  const { data, error } = await db.from("access_permissions").select("*").order("sort_order");
  if (error) throw error;
  return data ?? [];
}

export async function fetchRoles(): Promise<AccessRole[]> {
  const [roles, links] = await Promise.all([
    db.from("access_roles").select("id, key, name, description, is_system, assignable").order("is_system", { ascending: false }).order("name"),
    db.from("access_role_permissions").select("role_id, permission_key"),
  ]);
  if (roles.error) throw roles.error;
  if (links.error) throw links.error;
  const byRole = new Map<string, string[]>();
  for (const l of links.data ?? []) {
    byRole.set(l.role_id, [...(byRole.get(l.role_id) ?? []), l.permission_key]);
  }
  return (roles.data ?? []).map((r: Omit<AccessRole, "permissions">) => ({ ...r, permissions: byRole.get(r.id) ?? [] }));
}

export const fetchAssignments = () => rpc<AccessAssignment[]>("access_list_assignments");
export const fetchScopes = () => rpc<ScopeOptions>("access_list_scopes");
export const fetchAuditLog = (limit = 200) => rpc<AccessAuditEntry[]>("access_list_audit", { _limit: limit });
export const searchPeople = (query: string) => rpc<AccessPerson[]>("access_search_people", { _query: query });
export const fetchMyPermissions = () => rpc<MyPermission[]>("my_access_permissions");

export function saveRole(input: { id: string | null; name: string; description: string; permissions: string[] }) {
  return rpc<string>("access_save_role", {
    _role_id: input.id,
    _name: input.name,
    _description: input.description,
    _permissions: input.permissions,
  });
}

export const deleteRole = (roleId: string) => rpc<void>("access_delete_role", { _role_id: roleId });

export function assignRole(input: {
  userId: string;
  roleId: string;
  scopeType: ScopeType | null;
  scopeId: string | null;
  expiresAt: string | null;
}) {
  return rpc<void>("access_assign_role", {
    _user_id: input.userId,
    _role_id: input.roleId,
    _scope_type: input.scopeType,
    _scope_id: input.scopeId,
    _expires_at: input.expiresAt,
  });
}

export const revokeAssignment = (a: Pick<AccessAssignment, "source" | "id">) =>
  rpc<void>("access_revoke", { _source: a.source, _id: a.id });
